import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import {
  FaArrowRight,
  FaDownload,
  FaEnvelope,
  FaEye,
  FaFileInvoiceDollar,
} from 'react-icons/fa';
import PropTypes from 'prop-types';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import {
  YearOptionsForInvoice,
  QuarterOptionsForInvoice,
  selectDaysOptions,
  monthsSelectionsOptions,
  INVOICE_ACTIONS,
} from 'utils/StaticData';
import {
  FieldLabel,
  InvoiceActionPicker,
  SegmentedTabs,
} from './InvoiceActionPicker';

const PERIOD_TABS = selectDaysOptions.map((option) => ({
  value: String(option.id),
  label: option.label,
}));

const [QUARTER_PERIOD, MONTH_PERIOD] = selectDaysOptions;

/**
 * Single-merchant "Generate Invoice" dialog on the merchant detail page. Same
 * layout and rules as the bulk dialog, without the merchant picker: this dialog
 * is always about the merchant being viewed.
 *
 * RAT-1826: while the merchant's legal information is incomplete, only a preview
 * invoice can be produced - generating, downloading and emailing the official
 * invoice are refused here and in the backend.
 *
 * RAT-1854: `languageOptions` is supplied by the caller - English plus this
 * merchant's own local language - rather than being a fixed list of every
 * language the platform knows.
 */
const MerchantInvoiceModal = ({
  isOpen,
  onClose,
  handleSubmit,
  register,
  control,
  watch,
  reset,
  clearErrors,
  setValue,
  errors,
  handleGenerate,
  isPDFDownload,
  isEmailSending,
  isLegalInfoIncomplete,
  financeEmail,
  languageOptions,
}) => {
  const selectedAction = watch('invoiceAction') ?? '';
  const isQuarterPeriod = watch('daysSelect')?.id === QUARTER_PERIOD.id;
  const isBusy = isPDFDownload || isEmailSending;

  const officialActionState = (label) =>
    isLegalInfoIncomplete
      ? {
          isDisabled: true,
          disabledReason: `${label} is unavailable until the merchant's legal info is complete.`,
          disabledHint: 'Requires legal information',
        }
      : {};

  const ACTIONS = [
    {
      value: INVOICE_ACTIONS.PREVIEW,
      label: 'Preview Invoice',
      description: 'Unofficial copy, nothing is issued',
      icon: FaEye,
    },
    {
      value: INVOICE_ACTIONS.DOWNLOAD,
      label: 'Download Invoice',
      description: 'Download as PDF',
      icon: FaDownload,
      ...officialActionState('Downloading'),
    },
    {
      value: INVOICE_ACTIONS.EMAIL,
      label: 'Send Email',
      description: 'Send to the merchant',
      icon: FaEnvelope,
      ...(financeEmail
        ? officialActionState('Emailing')
        : {
            isDisabled: true,
            disabledReason: 'No finance email configured for this merchant.',
            disabledHint: 'No finance email set',
          }),
    },
  ];

  const onPeriodChange = (nextPeriod) => {
    const isQuarter = nextPeriod === String(QUARTER_PERIOD.id);
    setValue('daysSelect', isQuarter ? QUARTER_PERIOD : MONTH_PERIOD);
    if (isQuarter) {
      setValue('month', null);
      setValue('endMonth', null);
      clearErrors(['month', 'endMonth']);
    } else {
      setValue('quarter', null);
      clearErrors('quarter');
    }
  };

  const closeAndReset = () => {
    reset();
    clearErrors();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside"
      closeOnOverlayClick={false}
      size={{ base: 'sm', md: 'xl', lg: '2xl' }}
    >
      <ModalOverlay />
      <ModalContent
        mx={{ base: 3, md: 4 }}
        my={{ base: 4, md: 8 }}
        maxH={{ base: '92vh', md: '88vh' }}
        borderRadius={{ base: '12px', md: '16px' }}
      >
        <ModalHeader display="flex" gap={3} alignItems="flex-start" pb={3}>
          <Icon
            as={FaFileInvoiceDollar}
            color="primary"
            w="22px"
            h="22px"
            mt="2px"
          />
          <Box>
            <Text fontSize="lg" fontWeight="700">
              Generate Invoice
            </Text>
            <Text fontSize="sm" fontWeight="400" color="gray.500">
              Select details and choose an action to proceed.
            </Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton onClick={closeAndReset} />
        <ModalBody pt={0} px={{ base: 4, md: 6 }} overflowY="auto">
          <VStack spacing={4} align="stretch">
            {isLegalInfoIncomplete && (
              <Alert status="error" borderRadius="md" py={2}>
                <AlertIcon />
                <Text fontSize="sm">
                  Legal info incomplete. Only a preview invoice can be generated
                  for this merchant.
                </Text>
              </Alert>
            )}

            <Box>
              <FieldLabel>Time Period</FieldLabel>
              <SegmentedTabs
                options={PERIOD_TABS}
                value={String(watch('daysSelect')?.id ?? QUARTER_PERIOD.id)}
                onChange={onPeriodChange}
              />
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Controller
                name="year"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Year"
                    name="year"
                    type="select"
                    options={YearOptionsForInvoice}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    error={errors?.year}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                    placeholderValue="Year"
                  />
                )}
              />
              {/* Language keeps this slot in both period modes, so switching
                    between Quarters and Months never moves it. */}
              <Controller
                name="language"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Language"
                    name="language"
                    type="select"
                    options={languageOptions}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    error={errors?.language}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                    placeholderValue="Language"
                  />
                )}
              />
            </SimpleGrid>

            {/* The period row below: one full-width Quarter, or From/To month
                side by side. Both modes therefore keep the same two rows. */}
            {isQuarterPeriod && (
              <Controller
                name="quarter"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Quarter"
                    name="quarter"
                    type="select"
                    options={QuarterOptionsForInvoice}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    isRequired={true}
                    error={errors?.quarter}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                    placeholderValue="Select quarter"
                  />
                )}
              />
            )}

            {!isQuarterPeriod && (
              <Box>
                {/* RAT-1610: at most 6 consecutive months, within one year. */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <Controller
                    name="month"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="From Month"
                        name="month"
                        type="select"
                        options={monthsSelectionsOptions}
                        value={value ? [value] : []}
                        onChange={(selected) => {
                          onChange(selected);
                          const to = watch('endMonth');
                          if (
                            selected?.id &&
                            (!to?.id ||
                              to.id < selected.id ||
                              to.id > selected.id + 5)
                          ) {
                            setValue('endMonth', selected);
                            clearErrors('endMonth');
                          }
                        }}
                        register={register}
                        isRequired={true}
                        error={errors?.month}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                        placeholderValue="Select from month"
                      />
                    )}
                  />
                  <Controller
                    name="endMonth"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="To Month"
                        name="endMonth"
                        type="select"
                        options={monthsSelectionsOptions.filter(
                          (m) =>
                            !watch('month')?.id ||
                            (m.id >= watch('month').id &&
                              m.id <= watch('month').id + 5),
                        )}
                        value={value ? [value] : []}
                        onChange={onChange}
                        register={register}
                        isRequired={true}
                        isDisabled={!watch('month')?.id}
                        error={errors?.endMonth}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                        placeholderValue="Select to month"
                      />
                    )}
                  />
                </SimpleGrid>
              </Box>
            )}

            <InvoiceActionPicker
              actions={ACTIONS}
              value={selectedAction}
              onChange={(action) => setValue('invoiceAction', action)}
            />
          </VStack>
        </ModalBody>
        <ModalFooter
          gap={3}
          flexDirection={{ base: 'column-reverse', sm: 'row' }}
          px={{ base: 4, md: 6 }}
        >
          <Button
            type="button"
            variant="outline"
            fontSize="sm"
            fontWeight="500"
            minW="120px"
            w={{ base: '100%', sm: 'auto' }}
            onClick={closeAndReset}
          >
            Cancel
          </Button>
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            rightIcon={<Icon as={FaArrowRight} w="13px" h="13px" />}
            isLoading={isBusy}
            loadingText="Starting..."
            isDisabled={!selectedAction || isBusy}
            fontSize="sm"
            fontWeight="500"
            minW="160px"
            w={{ base: '100%', sm: 'auto' }}
            onClick={handleSubmit(() => handleGenerate(selectedAction)())}
          >
            Proceed
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

MerchantInvoiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  handleGenerate: PropTypes.func.isRequired,
  isPDFDownload: PropTypes.bool.isRequired,
  isEmailSending: PropTypes.bool.isRequired,
  isLegalInfoIncomplete: PropTypes.bool.isRequired,
  financeEmail: PropTypes.string,
  languageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default MerchantInvoiceModal;
